import { useCallback, useState } from 'react';

import {
  CAPABILITY_CARDS,
  CAPABILITY_INTRO,
  CONTACT_TILES,
  DOWNLOAD_COPY,
  FAQ_ITEMS,
  HERO_COPY,
  HERO_EYEBROW,
  HERO_HEADLINE,
  HOW_IT_WORKS_STEPS,
  MONEY_PANELS,
  PHONE_SCREENS,
  PROBLEM_CARDS,
  PROBLEM_HEADLINE,
  RENT_CATEGORIES,
  RENT_INTRO,
  RENT_NOTES,
  STORE_BADGES,
  TRUST_INTRO,
  TRUST_POINTS,
  WHY_SHOUTT,
  WHY_US_POINTS,
} from '@/features/home/constants/home.constants';
import { useDocumentTitle } from '@/hooks/useDocumentTitle.hook';
import { useHashScroll } from '@/hooks/useHashScroll.hook';
import type {
  CapabilityCard,
  ContactTile,
  DownloadCopy,
  FaqItem,
  HeroCopy,
  HeroHeadline,
  HowItWorksStep,
  MoneyPanel,
  PhoneScreen,
  ProblemCard,
  RentCategory,
  RentNote,
  SectionIntro,
  StoreBadge,
  TrustPoint,
  WhyShoutt,
  WhyUsPoint,
} from '@/types/home.types';

interface UseHomePageResult {
  heroEyebrow: string;
  heroHeadline: HeroHeadline;
  heroCopy: HeroCopy;
  phoneScreens: PhoneScreen[];
  activeScreenId: PhoneScreen['id'];
  onSelectScreen: (id: PhoneScreen['id']) => void;
  storeBadges: StoreBadge[];
  problemHeadline: string;
  problemCards: ProblemCard[];
  whyShoutt: WhyShoutt;
  whyUsPoints: WhyUsPoint[];
  capabilityIntro: SectionIntro;
  capabilityCards: CapabilityCard[];
  rentIntro: SectionIntro;
  rentCategories: RentCategory[];
  rentNotes: RentNote[];
  steps: HowItWorksStep[];
  trustIntro: SectionIntro;
  trustPoints: TrustPoint[];
  moneyPanels: MoneyPanel[];
  faqItems: FaqItem[];
  openFaqIndex: number;
  onToggleFaq: (index: number) => void;
  downloadCopy: DownloadCopy;
  contactTiles: ContactTile[];
}

export const useHomePage = (): UseHomePageResult => {
  useDocumentTitle(
    'Shoutt | Ask your campus. Anything.',
    'Shoutt is a campus-only app for verified college students in India. Rent out what you are not using, or post a Shoutt and a student nearby picks it up.',
  );

  // Header anchors are absolute (`/#trust`), so arriving from a legal page lands
  // on an empty shell before the sections exist.
  useHashScroll();

  const [activeScreenId, setActiveScreenId] = useState<PhoneScreen['id']>('feed');
  // The first answer starts open so the accordion never reads as an empty column.
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const onSelectScreen = useCallback((id: PhoneScreen['id']) => setActiveScreenId(id), []);

  const onToggleFaq = useCallback(
    (index: number) => setOpenFaqIndex((current) => (current === index ? -1 : index)),
    [],
  );

  return {
    heroEyebrow: HERO_EYEBROW,
    heroHeadline: HERO_HEADLINE,
    heroCopy: HERO_COPY,
    phoneScreens: PHONE_SCREENS,
    activeScreenId,
    onSelectScreen,
    storeBadges: STORE_BADGES,
    problemHeadline: PROBLEM_HEADLINE,
    problemCards: PROBLEM_CARDS,
    whyShoutt: WHY_SHOUTT,
    whyUsPoints: WHY_US_POINTS,
    capabilityIntro: CAPABILITY_INTRO,
    capabilityCards: CAPABILITY_CARDS,
    rentIntro: RENT_INTRO,
    rentCategories: RENT_CATEGORIES,
    rentNotes: RENT_NOTES,
    steps: HOW_IT_WORKS_STEPS,
    trustIntro: TRUST_INTRO,
    trustPoints: TRUST_POINTS,
    moneyPanels: MONEY_PANELS,
    faqItems: FAQ_ITEMS,
    openFaqIndex,
    onToggleFaq,
    downloadCopy: DOWNLOAD_COPY,
    contactTiles: CONTACT_TILES,
  };
};
